import React, { useState } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { Send as SendIcon, GetApp as GetAppIcon } from '@mui/icons-material';

const SendAppToPatient = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const defaultMessage = `Hi! Your physician has recommended the StrepApp mobile application to help with your healthcare needs. You can download it from the App Store: https://apps.apple.com/us/app/strepapp/id1544924313`;

  const handleSendMessage = async () => {
    if (!phoneNumber.trim()) {
      alert('Please enter a phone number');
      return;
    }

    setIsLoading(true);
    setSuccessMessage('');
    
    try {
      // Cloudflare Worker URL for sending SMS
      const workerUrl = 'https://strepapp-sms-worker.tasknotes.workers.dev/send-sms';
      
      const response = await fetch(workerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: phoneNumber.trim(),
          message: message || defaultMessage
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSuccessMessage(`SMS sent successfully to ${phoneNumber}!`);
        setPhoneNumber('');
        setMessage('');
      } else {
        throw new Error(result.error || 'Failed to send SMS');
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      alert(`Failed to send SMS: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-8">
      <Typography
        variant="h4"
        component="h1"
        align="center"
        gutterBottom
        className="mb-8"
      >
        Send App to Patient
      </Typography>

      <div className="max-w-2xl mx-auto">
        <Card className="mb-6">
          <CardContent>
            <Box className="flex items-center mb-4">
              <GetAppIcon className="mr-2 text-blue-500" />
              <Typography variant="h6">
                StrepApp Mobile Application
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" className="mb-4">
              Send your patients a text message with the download link for the StrepApp mobile application.
            </Typography>
            <Divider />
          </CardContent>
        </Card>

        <Paper className="p-6 rounded-lg shadow-lg">
          <Box className="space-y-4">
            <TextField
              fullWidth
              label="Patient Phone Number"
              placeholder="(555) 123-4567"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              variant="outlined"
              helperText="Enter the patient's US phone number"
            />

            <TextField
              fullWidth
              label="Message"
              multiline
              rows={4}
              value={message || defaultMessage}
              onChange={(e) => setMessage(e.target.value)}
              variant="outlined"
              helperText="Customize the message or use the default template"
            />

            <Box className="flex justify-between items-center pt-4">
              <Button
                variant="outlined"
                onClick={() => setMessage(defaultMessage)}
                disabled={isLoading}
              >
                Reset to Default
              </Button>
              
              <Button
                variant="contained"
                color="primary"
                onClick={handleSendMessage}
                disabled={isLoading || !phoneNumber.trim()}
                startIcon={<SendIcon />}
                className="ml-4"
              >
                {isLoading ? 'Sending...' : 'Send Message'}
              </Button>
            </Box>
          </Box>
        </Paper>

        {successMessage && (
          <Alert severity="info" className="mt-4">
            {successMessage}
          </Alert>
        )}

        <Card className="mt-6">
          <CardContent>
            <Typography variant="h6" className="mb-2">
              App Information:
            </Typography>
            <Typography variant="body2" color="textSecondary" className="mb-2">
              <strong>StrepApp iOS:</strong> https://apps.apple.com/us/app/strepapp/id1544924313
            </Typography>
            <Divider className="my-2" />
            <Typography variant="body2" color="textSecondary">
              <strong>Next Steps:</strong><br/>
              • Twilio SMS integration will be implemented in the next phase<br/>
              • Currently shows placeholder functionality for testing
            </Typography>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SendAppToPatient;
